// MF-67 — Journal Entries list (staff-created records). Filterable by text, category, type,
// status and date range; server-ordered by occurred date (newest first). Row actions: preview,
// follow-up, edit, delete-with-confirmation.
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, MenuItem, TextField, Typography } from '@mui/material'
import Module from '../../../layout/Module'
import RowActions from '../components/RowActions'
import ConfirmDelete from '../components/ConfirmDelete'
import PreviewDialog from '../components/PreviewDialog'
import EntryPreview from '../components/EntryPreview'
import FollowUpDialog from '../components/FollowUpDialog'
import ViewSwitch from '../components/ViewSwitch'
import JournalInsights from '../Insights/JournalInsights'
import ListSearch from '../components/ListSearch'
import ListTable, { Muted, Stacked } from '../components/ListTable'
import ColorBadge, { CodeTag, Points, StatusBadge } from '../components/ColorBadge'
import { inputSx } from '../components/formStyles'
import { useCategories, useSeveritiesMap, useTypes } from '../components/useLookups'
import { loadEntries } from '../../../../store/actions/journalEntriesActions'
import { deleteEntry } from '../../../../services/journal/entries.service'

const STATUS_OPTIONS = ['open', 'actioned', 'resolved']
const EMPTY_FILTERS = { search: '', categoryCode: '', type: '', status: '', from: '', to: '' }
const VIEW_KEY = 'journal.entries.view'
const readView = () => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'insights' ? 'insights' : 'list'
  } catch {
    return 'list'
  }
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—')
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString(undefined, { timeStyle: 'short' }) : '')

const studentsLabel = (students = []) => {
  if (!students.length) return null
  const first = students[0]?.name || '—'
  return students.length > 1 ? `${first} +${students.length - 1}` : first
}

const FilterSelect = ({ value, onChange, label, children, width = 150 }) => (
  <TextField
    select
    size='small'
    value={value}
    onChange={(e) => onChange(e.target.value)}
    SelectProps={{
      displayEmpty: true,
      SelectDisplayProps: { 'aria-label': label, 'aria-labelledby': undefined }
    }}
    sx={{ ...inputSx, width }}
  >
    {children}
  </TextField>
)

const EntriesList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { list, loading, totalRecords } = useSelector((state) => state.journalEntries.entries)
  const severities = useSeveritiesMap()
  const { list: types, byCode: typesByCode } = useTypes()
  const { list: categories, byCode: categoriesByCode } = useCategories()
  const [toDelete, setToDelete] = useState(null)
  const [preview, setPreview] = useState(null)
  const [followUp, setFollowUp] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  // List or Insights over the same filters; remembered per viewer (best effort).
  const [view, setViewState] = useState(readView)
  const setView = (v) => {
    setViewState(v)
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* storage unavailable: keep it in memory */
    }
  }

  const hasFilters = Object.values(filters).some((v) => v)

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(0)
  }

  // Build the query params, omitting empty filters.
  const params = useMemo(() => {
    const p = { page: page + 1, limit: rowsPerPage }
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v
    })
    return p
  }, [page, rowsPerPage, filters])

  const reload = () => dispatch(loadEntries(params))

  useEffect(() => {
    if (view === 'list') dispatch(loadEntries(params))
  }, [dispatch, params, view])

  const onConfirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteEntry(toDelete._id)
      setToDelete(null)
      reload()
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      key: 'occurred',
      label: 'Occurred',
      width: 130,
      render: (r) => <Stacked primary={fmtDate(r.occurredAt)} secondary={fmtTime(r.occurredAt)} />
    },
    {
      key: 'entry',
      label: 'Entry',
      render: (r) => {
        const cat = categoriesByCode.get(r.categoryCode)?.name || r.categoryCode
        const tpl = r.template?.title
        return (
          <Stacked
            primary={r.overview || tpl || '—'}
            secondary={[r.overview ? tpl : null, cat].filter(Boolean).join(' · ')}
          />
        )
      }
    },
    {
      key: 'students',
      label: 'Students',
      width: 190,
      render: (r) => {
        const label = studentsLabel(r.students)
        return label ? (
          <span title={(r.students || []).map((s) => s.name).join(', ')}>{label}</span>
        ) : (
          <Muted>—</Muted>
        )
      }
    },
    {
      key: 'severity',
      label: 'Severity',
      width: 230,
      render: (r) => {
        const rt = severities.get(r.severity)
        if (!r.severity || !typesByCode.get(r.type)?.usesSeverity)
          return (
            <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
              {typesByCode.get(r.type)?.usesSeverity ? 'Not set' : 'Not applicable'}
            </span>
          )
        return rt ? <ColorBadge label={rt.label} color={rt.color} size='sm' /> : <CodeTag code={r.severity} />
      }
    },
    {
      key: 'points',
      label: 'Points',
      width: 80,
      align: 'center',
      render: (r) => <Points value={r.points ?? 0} />
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (r) => <StatusBadge status={r.status || 'open'} />
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 240,
      align: 'right',
      render: (r) => (
        <RowActions
          onPreview={() => setPreview(r)}
          onFollowUp={() => setFollowUp(r)}
          onEdit={() => navigate(`/dashboard/journal/entries/edit/${r._id}`)}
          onDelete={() => setToDelete(r)}
        />
      )
    }
  ]

  return (
    <Module>
      <Module.Header
        moduleName='journalentries'
        title='Journal Entries'
        titleLink='/dashboard/journal/entries'
      >
        <div className='flex justify-end items-center gap-2'>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => navigate('/dashboard/journal/entries/add')}
          >
            New Entry
          </Button>
        </div>
      </Module.Header>
      <Module.BodyContainer className='overflow-y-auto'>
        <div style={{ padding: '8px 8px 24px' }}>
          <div style={{ paddingBottom: 12 }}>
            <ViewSwitch
              options={[
                { value: 'list', label: 'Entries' },
                { value: 'insights', label: 'Insights' }
              ]}
              value={view}
              onChange={setView}
            />
          </div>
          <ListSearch
            value={filters.search}
            placeholder='Search overview or student…'
            onChange={(v) => setFilter('search', v)}
          >
            <FilterSelect
              label='Category'
              value={filters.categoryCode}
              onChange={(v) => setFilter('categoryCode', v)}
              width={200}
            >
              <MenuItem value=''>All categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c._id || c.code} value={c.code}>
                  {c.name || c.code}
                </MenuItem>
              ))}
            </FilterSelect>
            <FilterSelect label='Type' value={filters.type} onChange={(v) => setFilter('type', v)}>
              <MenuItem value=''>All types</MenuItem>
              {types.map((sv) => (
                <MenuItem key={sv.code} value={sv.code}>
                  {sv.label}
                </MenuItem>
              ))}
            </FilterSelect>
            <FilterSelect label='Status' value={filters.status} onChange={(v) => setFilter('status', v)}>
              <MenuItem value=''>All statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {cap(s)}
                </MenuItem>
              ))}
            </FilterSelect>
            <TextField
              type='date'
              size='small'
              value={filters.from}
              onChange={(e) => setFilter('from', e.target.value)}
              inputProps={{ 'aria-label': 'From date', max: filters.to || undefined }}
              sx={{ ...inputSx, width: 160 }}
            />
            <span style={{ color: 'rgb(148, 163, 184)' }}>–</span>
            <TextField
              type='date'
              size='small'
              value={filters.to}
              onChange={(e) => setFilter('to', e.target.value)}
              inputProps={{ 'aria-label': 'To date', min: filters.from || undefined }}
              sx={{ ...inputSx, width: 160 }}
            />
            {hasFilters ? (
              <Button
                size='small'
                color='inherit'
                onClick={() => {
                  setFilters(EMPTY_FILTERS)
                  setPage(0)
                }}
              >
                Clear filters
              </Button>
            ) : null}
          </ListSearch>
          {view === 'insights' ? (
            <JournalInsights filters={Object.fromEntries(Object.entries(filters).filter(([, v]) => v))} />
          ) : (
            <>
              {!loading ? (
                <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)', margin: '-4px 0 12px' }}>
                  {totalRecords.toLocaleString()} {totalRecords === 1 ? 'entry' : 'entries'}
                  {hasFilters ? ' match the filters' : ''}
                </Typography>
              ) : null}
              <ListTable
                columns={columns}
                rows={list}
                loading={loading}
                minWidth={1080}
                emptyText={hasFilters ? 'No entries match the filters' : 'No journal entries yet'}
                pagination={{
                  count: totalRecords,
                  page,
                  rowsPerPage,
                  onPageChange: setPage,
                  onRowsPerPageChange: (n) => {
                    setRowsPerPage(n)
                    setPage(0)
                  }
                }}
              />
            </>
          )}
        </div>
      </Module.BodyContainer>

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='entry'
        name={toDelete?.overview || toDelete?.template?.title}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <PreviewDialog
        open={Boolean(preview)}
        title={preview?.overview || preview?.template?.title || 'Journal entry'}
        onClose={() => setPreview(null)}
        maxWidth='md'
      >
        {preview ? <EntryPreview entry={preview} onChange={() => reload()} /> : null}
      </PreviewDialog>

      {followUp ? (
        <FollowUpDialog
          entry={followUp}
          onClose={() => setFollowUp(null)}
          onSaved={() => {
            setFollowUp(null)
            reload()
          }}
        />
      ) : null}
    </Module>
  )
}

export default EntriesList
